import React from 'react';

/**
 * 時間軸の帯（0:00–24:00 固定）。
 *
 * ## 画面をまたいで使い回す
 *
 * シフト作成（希望 + 予定）と、予定と実績（予定 + 実績）で同じ形を使う。
 * **1画面に載せる帯は2本まで**（代表判断 2026-08-29）。3本並べると、
 * どれがどれか読めなくなる。
 *
 * ## ⚠️ 軸は 0:00–24:00 で固定する
 *
 * 日ごとに伸縮させると、日をまたいで見比べたときに同じ位置が同じ時刻でなくなる。
 * 日跨ぎの勤務は 24:00 で切り、残りを翌日の 0:00 から描く（`utils/splitAcrossDays.ts`）。
 */

/** 0:00 からの分。日跨ぎは呼び出し側で 24:00 で切ってある */
export interface Seg {
  start: number;
  end: number;
  /** 前日から続いている */
  fromPrev?: boolean;
  /** 翌日へ続く */
  toNext?: boolean;
  /** 終わりが未確定（退勤未打刻など） */
  open?: boolean;
  /** **まだ確定していない予定**（下書き）。破線で描き分ける */
  draft?: boolean;
}

/** 帯1本ぶんの見た目 */
export interface SeriesStyle {
  label: string;
  /** 'outline' = 枠線 / 'fill' = 塗り / 'ghost' = 薄い塗り（候補・希望） */
  kind: 'outline' | 'fill' | 'ghost';
  color: string;
}

/**
 * 帯の色は**画面をまたいで固定**する(代表指摘 2026-08-31)。
 *
 * 「シフトを組む」の予定を濃い青の塗りにしていたが、「予定と実績」では
 * **同じ濃い青が実績**を指していた。同じ色が画面によって別のものを指すと
 * 読み違える。ここに集めて、各画面はこれを渡すだけにする。
 *
 *   希望 = 薄いグレー / 予定 = 水色の枠線 / 実績 = 濃い青の塗り
 */
export const SERIES: Record<'wish' | 'plan' | 'actual', SeriesStyle> = {
  wish: { label: '希望', kind: 'ghost', color: '#64748B' },
  plan: { label: '予定', kind: 'outline', color: '#93C5FD' },
  actual: { label: '実績', kind: 'fill', color: '#2563EB' },
};

export interface TimelineRow {
  userId: number;
  /** 上段の帯 */
  upper: Seg[];
  /** 下段の帯 */
  lower: Seg[];
  /** 行に出す短い注記（「打刻なし」など）。**出すのは読めない理由があるときだけ** */
  note?: string;
  noteColor?: string;
}

const DAY = 24 * 60;
const hm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

const pos = (from: number, to: number) => ({
  left: `${(from / DAY) * 100}%`,
  width: `${(Math.max(4, to - from) / DAY) * 100}%`,
});

const segStyle = (r: Seg, st: SeriesStyle, top: number): React.CSSProperties => ({
  position: 'absolute',
  top,
  height: 13,
  boxSizing: 'border-box',
  borderRadius: `${r.fromPrev ? 0 : 3}px ${r.toNext ? 0 : 3}px ${r.toNext ? 0 : 3}px ${r.fromPrev ? 0 : 3}px`,
  ...(st.kind === 'outline'
    ? {
        /*
         * 下書きは**破線だけで分ける**（代表指摘 2026-09-02）。
         * 色まで変えるとグレーの別物に見えて、同じ「予定」だと読めない。
         * 違うのは確定しているかどうかだけなので、色は予定のまま。
         */
        border: `2px ${r.draft ? 'dashed' : 'solid'} ${st.color}`,
        background: '#EFF6FF',
        borderLeftWidth: r.fromPrev ? 0 : 2,
        borderRightWidth: r.toNext ? 0 : 2,
      }
    : st.kind === 'ghost'
      ? { background: st.color, opacity: 0.35, border: `1px dashed ${st.color}` }
      : r.open
        ? /*
           * 退勤が未打刻。**右へ消える**形にする（代表指摘 2026-08-31）。
           * 薄い塗りにしていたが、予定（水色の枠）とほぼ同じ色に見えて
           * 「短い予定」と読めた。終わりが分からないことを形で出す。
           */
          { background: `linear-gradient(to right, ${st.color}CC, ${st.color}00)`, borderRadius: '3px 0 0 3px' }
        : { background: st.color }),
  ...pos(r.start, r.end),
});

interface Props {
  rows: TimelineRow[];
  nameOf: (userId: number) => string;
  upper: SeriesStyle;
  lower: SeriesStyle;
  /** 行を押したときの動作。渡さなければ押せない */
  onPick?: (userId: number) => void;
  /** 空のときの文言 */
  emptyText?: string;
}

const ShiftTimeline: React.FC<Props> = ({ rows, nameOf, upper, lower, onPick, emptyText }) => {
  if (rows.length === 0) {
    return <p style={{ color: '#6c757d', fontSize: 13, margin: '12px 0' }}>{emptyText ?? 'この日は何もありません。'}</p>;
  }

  // 目盛りは2時間ごと。24本すべてに文字を置くと読めない
  const labels = Array.from({ length: 13 }, (_, i) => i * 120);
  const gridLines = Array.from({ length: 23 }, (_, i) => (i + 1) * 60);

  const legend = (st: SeriesStyle) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 22, height: 11, borderRadius: 3, boxSizing: 'border-box', ...(st.kind === 'outline' ? { border: `2px solid ${st.color}`, background: '#EFF6FF' } : st.kind === 'ghost' ? { background: st.color, opacity: 0.35, border: `1px dashed ${st.color}` } : { background: st.color }) }} />
      {st.label}
    </span>
  );

  /*
   * 退勤が未打刻の帯は薄く短く描いている(長さに意味は無い)。
   * **凡例が無いと「短い実績」と読める**(代表指摘 2026-08-31)ので、
   * そういう帯がある日だけ凡例を足す。常に出すと普段の目に余計な行が増える。
   */
  const hasOpen = rows.some((r) => r.upper.some((s) => s.open) || r.lower.some((s) => s.open));
  /** 下書きの破線も同じ。描き分けている以上、読み方を書いておく */
  const hasDraft = rows.some((r) => r.upper.some((s) => s.draft) || r.lower.some((s) => s.draft));

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 700 }}>
        <div style={{ position: 'relative', marginLeft: 150, height: 16, marginBottom: 4 }}>
          {labels.map((m) => (
            <span
              key={m}
              style={{
                position: 'absolute',
                left: `${(m / DAY) * 100}%`,
                transform: m === 0 ? 'none' : m === DAY ? 'translateX(-100%)' : 'translateX(-50%)',
                fontSize: 10,
                color: '#94A3B8',
                whiteSpace: 'nowrap',
              }}
            >
              {m === DAY ? '24' : String(m / 60)}
            </span>
          ))}
        </div>

        {rows.map((row) => (
          <div key={row.userId} style={{ display: 'flex', alignItems: 'center', minHeight: 42, borderTop: '1px solid #F1F5F9' }}>
            <div style={{ width: 150, flexShrink: 0, paddingRight: 8, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nameOf(row.userId)}
            </div>

            <div
              onClick={() => onPick?.(row.userId)}
              style={{ position: 'relative', flex: 1, height: 34, cursor: onPick ? 'pointer' : 'default' }}
            >
              {gridLines.map((h) => (
                <div
                  key={h}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${(h / DAY) * 100}%`,
                    width: 1,
                    background: h % 180 === 0 ? '#E2E8F0' : '#F5F7FA',
                  }}
                />
              ))}

              {row.upper.map((r, i) => (
                <div key={`u${i}`} title={`${upper.label} ${hm(r.start)}–${r.toNext ? '24:00（翌日へ続く）' : hm(r.end)}`} style={segStyle(r, upper, 2)} />
              ))}
              {row.lower.map((r, i) => (
                <div key={`l${i}`} title={r.open ? `${lower.label} ${hm(r.start)}–（未確定）` : `${lower.label} ${hm(r.start)}–${r.toNext ? '24:00（翌日へ続く）' : hm(r.end)}`} style={segStyle(r, lower, 19)} />
              ))}

              {row.note && (
                <div style={{ position: 'absolute', top: 19, left: 4, fontSize: 11, color: row.noteColor ?? '#991B1B', fontWeight: 700 }}>
                  {row.note}
                </div>
              )}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 18, marginTop: 10, marginLeft: 150, fontSize: 12, color: '#64748B', flexWrap: 'wrap' }}>
          {legend(upper)}
          {legend(lower)}
          {hasDraft && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 22, height: 11, borderRadius: 3, boxSizing: 'border-box', border: `2px dashed ${upper.kind === 'outline' ? upper.color : lower.color}`, background: '#EFF6FF' }} />
              下書き（未確定）
            </span>
          )}
          {hasOpen && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 22, height: 11, borderRadius: '3px 0 0 3px', background: `linear-gradient(to right, ${lower.color}CC, ${lower.color}00)` }} />
              退勤が未打刻
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftTimeline;
